import { Loadable, useRecoilRefresher_UNSTABLE, useRecoilState, useRecoilValue, useRecoilValueLoadable } from "recoil";
import { useEffect, useState } from "react";
import moment from "moment";
import { Button, Chip, Dialog, DialogContent, DialogTitle, List, ListItem, Stack, Typography } from "@mui/material";
import { DataGridPro, gridClasses, GridColDef, GridSortModel } from "@mui/x-data-grid-pro";
import {
  BACKTEST_HISTORIES_DEFAULT_SORT,
  staticBacktestHistoriesQuery,
  staticBacktestHistoriesQueryPage,
  staticBacktestHistoriesQueryPageSize,
  staticBacktestHistoriesSort,
} from "@states/staticBacktest";
import useAuth from "@hooks/useAuth";
import axios from "@utils/axios";
import { IPagination } from "types/api";
import { IResBacktestHistory } from "types/backtest";
import { PERMISSIONS, useCan } from "@auth";

/**
 * 화면 필드명 → **DB 컬럼명**. 여기 없는 컬럼은 서버가 정렬할 수 없다.
 *
 * `backtest_histories` 테이블에는 `id · name · config · user_id · created_at` 밖에
 * 없다. 시작날짜·종료날짜·거래비용·벤치마크·주기·통화·배당세는 전부 **`config`
 * JSON 안**이라 SQL 한 줄로 줄을 세울 수 없다. 그래서 그 컬럼들은 `sortable: false`
 * 로 막는다 — 누를 수 있게 두면 "눌렀는데 현재 페이지만 바뀐다" 가 된다.
 */
const SORT_FIELD_TO_COLUMN: Record<string, string> = {
  name: "name",
  createdAt: "created_at",
};

export default function BacktestHistoryDialog({ open, onSelect, onClose }) {
  const { user: me } = useAuth();
  const [page, setPage] = useRecoilState(staticBacktestHistoriesQueryPage);
  const [pageSize, setPageSize] = useRecoilState(staticBacktestHistoriesQueryPageSize);
  const [sort, setSort] = useRecoilState(staticBacktestHistoriesSort);
  const can = useCan();

  const backtestHistoriesLoadable: Loadable<IPagination<IResBacktestHistory>> = useRecoilValueLoadable<IPagination<IResBacktestHistory>>(
    staticBacktestHistoriesQuery
  );
  const backtestHistories: IPagination<IResBacktestHistory> = backtestHistoriesLoadable.contents;

  //https://mui.com/x/react-data-grid/pagination/#basic-implementation
  const [rowCountState, setRowCountState] = useState(
    backtestHistories.total || 0,
  );
  useEffect(() => {
    setRowCountState((prevRowCountState) =>
      backtestHistories.total !== undefined
        ? backtestHistories.total
        : prevRowCountState,
    );
  }, [backtestHistories.total, setRowCountState]);

  const backtestHistoriesRefresher = useRecoilRefresher_UNSTABLE(staticBacktestHistoriesQuery);

  // 그리드는 화면 필드명으로 말하고 서버는 DB 컬럼명으로 듣는다. 양방향으로 번역한다.
  const sortModel: GridSortModel = (() => {
    const field = Object.keys(SORT_FIELD_TO_COLUMN).find(
      (key) => SORT_FIELD_TO_COLUMN[key] === sort.field,
    );
    return field ? [{ field, sort: sort.order }] : [];
  })();

  const changeSort = (model: GridSortModel) => {
    // 정렬을 바꾸면 순서가 통째로 달라진다. 지금 페이지에 머무르면 방금 누른 것과
    // 상관없는 구간을 보게 되므로 첫 페이지로 돌린다.
    setPage(0);
    const next = model[0];
    const column = next ? SORT_FIELD_TO_COLUMN[next.field] : undefined;
    // 세 번째 클릭은 정렬 해제다. 서버는 무정렬을 모르므로 기본값으로 돌아간다.
    setSort(
      column && next?.sort ? { field: column, order: next.sort } : BACKTEST_HISTORIES_DEFAULT_SORT,
    );
  };

  const backtestHistoryColumns: GridColDef[] = [
    // { field: "id", headerName: "ID", width: 70 },
    { field: "name", headerName: "이름", width: 150 },
    { field: "bt_start", headerName: "시작날짜", width: 100, sortable: false, valueGetter: (params) => params.row.config.bt_start },
    { field: "bt_end", headerName: "종료날짜", width: 100, sortable: false, valueGetter: (params) => params.row.config.bt_end },
    {
      field: "strategies-assets",
      headerName: "전략-포트폴리오",
      sortable: false,
      width: 300,
      renderCell: (params) => (
        <List className="flex"
              sx={{
                width: '100%',
                maxWidth: 360,
                // bgcolor: 'background.paper',
                position: 'relative',
                overflow: 'auto',
                maxHeight: 300,
                '& ul': { padding: 0 },
              }}
              subheader={<li/>}
        >
          {params.row.config.strategies.map((strategy, index) => (
            <ListItem key={`st-${index}`}>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  justifyContent: "center",
                  alignItems: "flex-start",
                }}
              >
                <Typography noWrap>{`${strategy.name}: `}</Typography>
                {strategy.assets.map((asset) => (
                  <Chip label={`${asset.name}: ${asset.weight}%`} size="small" variant="outlined"/>
                ))}
              </Stack>
            </ListItem>
          ))}
        </List>
      ),
    },
    {
      field: "data_freq",
      headerName: "주기",
      sortable: false,
      width: 70,
      type: "string",
      renderCell: (params) => (
        <Chip
          label={params.row.config.data_freq}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: "currency",
      headerName: "통화",
      sortable: false,
      width: 70,
      type: "string",
      renderCell: (params) => (
        <Chip
          label={(params.row.config.currency === "USD") ? "$" : "₩"}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    { field: "cost", headerName: "거래비용", width: 70, sortable: false, valueGetter: (params) => `${params.row.config.cost * 100}%` },
    { field: "benchmark", headerName: "벤치마크 지수", width: 120, sortable: false, valueGetter: (params) => params.row.config.benchmark },
    ...(can(PERMISSIONS.BACKTEST_AFTER_TAX) ? [{
      field: "after_tax",
      headerName: "배당세 반영",
      sortable: false,
      width: 70,
      renderCell: (params) => (
        <Chip
          label={(params.row.config.after_tax === true) ? "Y" : "N"}
          size="small"
          color={(params.row.config.after_tax === true) ? "secondary" : "primary"}
          variant="outlined"
        />
      ),
    }] : []),
    {
      field: "createdAt",
      headerName: "실행일시",
      width: 150,
      renderCell: (params) =>
        moment(new Date(params.value), "h:mm:ss A")
          .add(9, "hour")
          .format("YYYY-MM-DD HH:mm:ss"),
    },
    // { field: "user", headerName: "사용자", width: 150, type: "string" },
    ...[{
      field: "action",
      headerName: "삭제",
      sortable: false,
      width: 70,
      renderCell: (params) =>
        (params.row.userId === me?.id) ? (
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={(e) => {
              const deleteConfirm = window.confirm(
                `"${params.row.name}" 기록을 삭제하시겠습니까?`
              );
              if (deleteConfirm) {
                deleteBacktestHistory(params.row.id);
              }
              e.stopPropagation();
            }}
          >
            삭제
          </Button>
        ) : null,
    }],
  ];

  const deleteBacktestHistory = async (id: number) => {
    await axios.delete(`/backtest/histories/${id}`);
    backtestHistoriesRefresher();
  };

  return (
    <Dialog
      fullWidth={true}
      maxWidth="lg"
      open={open}
      onClose={() => {
        setPage(0);
        onClose();
      }}
      aria-labelledby="max-width-dialog-title"
    >
      <DialogTitle id="max-width-dialog-title">내 백테스트 실행 기록</DialogTitle>
      <DialogContent style={{ height: 600, width: "100%" }}>
        <DataGridPro
          rows={backtestHistories.items || []}
          rowCount={rowCountState}
          loading={backtestHistoriesLoadable.state === "loading"}
          // rowsPerPageOptions={[5, 10, 50]}
          pagination
          page={page}
          pageSize={pageSize}
          paginationMode="server"
          sortingMode="server"
          // 정렬 해제 상태를 만들지 않는다. 서버에는 "무정렬" 이 없어서 해제하면
          // 기본 정렬로 돌아가는데, 기본 정렬이 걸린 칸(가입일·실행일시)을 누르면
          // "해제 → 기본값" 이라 **아무 일도 안 일어난다.** 눌렀는데 반응이 없는 것은
          // 고장으로 보인다. 오름/내림만 오가게 한다.
          sortingOrder={["asc", "desc"]}
          sortModel={sortModel}
          onSortModelChange={changeSort}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          columns={backtestHistoryColumns}
          initialState={{
            pinnedColumns: { left: ["id"] }
          }}
          density="compact"
          // components={{ Toolbar: CSToolbar }}
          getRowId={(row) => row.id}
          onRowClick={(param, evt) => {
            setPage(0);
            onSelect(param.row.config);
          }}
          getEstimatedRowHeight={() => 200}
          getRowHeight={() => 'auto'}
          sx={{
            [`& .${gridClasses.cell}`]: {
              py: 1,
            },
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
  